import { BooleanValidation, StringValidation } from '../../../utils/component-validations';
import { bool, or } from 'macro-decorators';
import { element } from 'ember-element-helper';
import { htmlSafe } from '@ember/template';
import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { verifyHtmlTag } from '../../../utils/spds/html-verification';
import Super from './base.gjs';
import styles from './container.module.scss';

export const CSS_PROPS = [];
export const DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES = 'initial';
export const ELEMENT_TAG_DEFAULT = 'div';
export const FLEX_AND_GRID_PROPS = [
  'alignContent',
  'alignItems',
  'alignSelf',
  'gap',
  'justifyContent',
];
export const FLEX_ONLY_PROPS = ['flex', 'flexWrap', 'order'];
export const GRID_CONVENIENCE_PROPS = ['area', 'areas', 'autoFlow', 'columns', 'rows'];
export const GRID_ONLY_PROPS = [
  'gridArea',
  'gridAutoColumns',
  'gridAutoFlow',
  'gridAutoRows',
  'gridColumn',
  'gridRow',
  'gridTemplateAreas',
  'gridTemplateColumns',
  'gridTemplateRows',
  'justifyItems',
  'justifySelf',
];

export default class SpdsCoreContainer extends Super {
  baseArguments = [
    new BooleanValidation({
      optional: [
        'absolute',
        'discardClick',
        'error',
        'fitContent',
        'fitContentHeight',
        'fitContentWidth',
        'fixed',
        'grid',
        'inline',
        'inputContainer',
        'subgrid',
      ],
    }),
    new StringValidation({
      optional: ['content', 'tag', ...CSS_PROPS, ...FLEX_AND_GRID_PROPS],
    }),
  ];
  baseClass = 'spds-container';
  classesToMapToArgs = ['absolute', 'error', 'fixed', 'inline', 'inputContainer', 'subgrid'];
  classesToMapToAttrs = ['column', 'fitContentHeight', 'fitContentWidth', 'grid', 'reverse'];
  defaultGap = DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  styles = styles;

  @service fastboot;

  @bool('args.fitContent') fitContent;
  @bool('args.fitContentHeight') fitContentH;
  @bool('args.fitContentWidth') fitContentW;
  @bool('args.discardClick') shouldDiscardClick;

  @or('fitContent', 'fitContentH') fitContentHeight;
  @or('fitContent', 'fitContentW') fitContentWidth;

  get arguments() {
    let stringValidations = {};
    let booleanValidations = {};

    if (this.grid) {
      // Grid mode: allow grid properties, forbid flex-only properties
      stringValidations.optional = [...GRID_ONLY_PROPS, ...GRID_CONVENIENCE_PROPS];
      stringValidations.forbidden = FLEX_ONLY_PROPS.map(name => ({
        name,
        reason: 'Flexbox-only properties cannot be used when @grid is set',
      }));
      booleanValidations.forbidden = [
        {
          name: 'column',
          reason: '@column can only be used with flexbox layouts',
        },
        {
          name: 'reverse',
          reason: '@reverse can only be used with flexbox layouts',
        },
      ];
    } else {
      booleanValidations.optional = ['column', 'reverse'];
      stringValidations.optional = [...FLEX_ONLY_PROPS];
      stringValidations.forbidden = [
        ...GRID_ONLY_PROPS.map(name => ({
          name,
          reason: '@grid must be set to use grid properties',
        })),
        ...GRID_CONVENIENCE_PROPS.map(name => ({
          name,
          reason: '@grid must be set to use grid properties',
        })),
      ];
    }

    return [
      ...this.baseArguments,
      new StringValidation(stringValidations),
      new BooleanValidation(booleanValidations),
    ];
  }

  get propsToMapToAttrs() {
    return [
      ...CSS_PROPS,
      ...FLEX_AND_GRID_PROPS,
      ...(this.grid ? GRID_ONLY_PROPS : FLEX_ONLY_PROPS),
    ];
  }

  get alignContent() {
    return this.args.alignContent ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get alignItems() {
    return this.args.alignItems ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get alignSelf() {
    return this.args.alignSelf ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get column() {
    return this.args.column && !this.grid;
  }

  get content() {
    return htmlSafe(this.args.content);
  }

  get flex() {
    return this.args.flex ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get flexWrap() {
    return this.args.flexWrap ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get gap() {
    return this.args.gap ?? this.defaultGap;
  }

  get grid() {
    return this.args.grid;
  }

  get gridArea() {
    return this.args.gridArea ?? this.args.area ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get gridAutoColumns() {
    return this.args.gridAutoColumns ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get gridAutoFlow() {
    return this.args.gridAutoFlow ?? this.args.autoFlow ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get gridAutoRows() {
    return this.args.gridAutoRows ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get gridColumn() {
    return this.args.gridColumn ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get gridRow() {
    return this.args.gridRow ?? this.args.row ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get gridTemplateAreas() {
    return this.args.gridTemplateAreas ?? this.args.areas ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get gridTemplateColumns() {
    return (
      this.args.gridTemplateColumns ?? this.args.columns ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES
    );
  }

  get gridTemplateRows() {
    return this.args.gridTemplateRows ?? this.args.rows ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get justifyContent() {
    return this.args.justifyContent ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get justifyItems() {
    return this.args.justifyItems ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get justifySelf() {
    return this.args.justifySelf ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get order() {
    return this.args.order ?? DEFAULT_VALUE_FOR_CUSTOM_PROPERTIES;
  }

  get reverse() {
    return this.args.reverse && !this.grid;
  }

  get tag() {
    let { tag = ELEMENT_TAG_DEFAULT } = this.args;

    if (this.fastboot?.isFastBoot) return tag;

    return verifyHtmlTag(ELEMENT_TAG_DEFAULT, tag);
  }

  setupDiscardClick = modifier(() => {
    if (this.clickHandlerSetup || !this.shouldDiscardClick || !this.baseElement) return;

    this.clickHandlerSetup = true;

    this.baseElement.addEventListener('click', this.ignoreClick);

    return () => {
      this.baseElement.removeEventListener('click', this.ignoreClick);
    };
  });

  ignoreClick = evt => {
    evt.stopPropagation();
  };

  <template>
    {{#let (element this.tag) as |Tag|}}
      <Tag
        class={{this.classes}}
        data-test-spds-container={{this.tag}}
        style={{this.customProperties}}
        {{this.setupComponent}}
        {{this.setupDiscardClick}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if @content}}
          {{this.content}}
        {{else}}
          {{yield}}
        {{/if}}
      </Tag>
    {{/let}}
  </template>
}
